import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { LoginInput, loginSchema } from "./config/authValidations";
import { pb } from "../../providers/pocketbase/pocketbase";
import { AuthLayout } from "../components/layout/AuthLayout";
import LanguageSwitcher from "../components/utils/LanguageSwitcher";
import { Environement } from "../../utils/Environement";
import Logger from "../../utils/Logger";

export default function SignInPage() {
  const location = useLocation();
  const message = location.state?.message;
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema(t)),
  });

  const onSubmit = async (data: LoginInput) => {
    try {
      setError(null);
      const { record } = await pb
        .collection("admins")
        .authWithPassword(data.email, data.password);
      if (!record) throw new Error("User not found");

      Logger.debug("sign in success", record);

      // redirection from event listener
    } catch (error) {
      Logger.error("sign in error", error);
      setError(t("auth.errors.generic"));
    }
  };

  return (
    <AuthLayout title={t("auth.login.title")}>
      <form
        onSubmit={handleSubmit(onSubmit, (errors) => Logger.warn(errors))}
        className="space-y-4"
      >
        {message && (
          <div className="bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 text-green-600 dark:text-green-200 px-4 py-3 rounded">
            {message}
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-600 dark:text-red-200 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t("auth.login.email")}
          </label>
          <div className="mt-1">
            <input
              id="email"
              type="email"
              autoComplete="email"
              className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              {...register("email")}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {t(errors.email.message ?? "")}
              </p>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t("auth.login.password")}
          </label>
          <div className="mt-1">
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              {...register("password")}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {t(errors.password.message ?? "")}
              </p>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="language"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t("auth.login.language")}
          </label>
          <LanguageSwitcher />
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm">
            <Link
              to="/forgot-password"
              className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {t("auth.login.forgotPassword")}
            </Link>
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t("auth.login.loading") : t("auth.login.submit")}
          </button>
        </div>

        {Environement.envIsDev && (
          <div className="text-sm text-center">
            <Link
              to="/signup"
              className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {t("auth.login.noAccount")}
            </Link>
          </div>
        )}
      </form>
    </AuthLayout>
  );
}
